/* global BigInt */

export const MONEY_FIELDS = ['salary_tiers', 'reward_config', 'daily_reward_config',
    'admin_salary_config', 'admin_reward_config', 'reward_bonus_config', 'reward_bonus_enabled'];
export const FIELD_LABELS = {
    salary_tiers: 'Процент фотографа', reward_config: 'Премия за чек',
    daily_reward_config: 'Премия за день', admin_salary_config: 'Оклад администратора',
    admin_reward_config: 'Премия администратора', reward_bonus_config: 'Дополнительная премия',
    reward_bonus_enabled: 'Дополнительная премия включена',
};
export const copy = value => JSON.parse(JSON.stringify(value));
const stable = value => Array.isArray(value) ? value.map(stable)
    : value && typeof value === 'object'
        ? Object.fromEntries(Object.keys(value).sort().map(key => [key, stable(value[key])])) : value;
export const equal = (a, b) => JSON.stringify(stable(a)) === JSON.stringify(stable(b));
export function moneyPatch(form, initial) {
    return Object.fromEntries(MONEY_FIELDS.filter(key => !equal(form[key], initial[key]))
        .map(key => [key, copy(form[key])]));
}
export const configLabel = config => config
    ? `${config.name || config.location_name || 'Шаблон'} · №${config.id}` : 'Автоматические условия отключены';
export const dayLabel = day => day === '1000-01-01' ? 'Вся история' : day;

// Денежные строки считаем без Number/parseFloat: точность не теряется.
function decimal(value) {
    const match = /^(-?)(\d+)(?:\.(\d+))?$/.exec(String(value ?? '0'));
    if (!match) throw new Error('Некорректная денежная сумма в ответе сервера.');
    const fraction = match[3] || '';
    return { units: BigInt(`${match[1]}${match[2]}${fraction}`), scale: fraction.length };
}
function textDecimal(units, scale) {
    const negative = units < 0n;
    const digits = (negative ? -units : units).toString().padStart(scale + 1, '0');
    return `${negative ? '-' : ''}${scale ? `${digits.slice(0, -scale)}.${digits.slice(-scale)}` : digits}`;
}
export function subtractMoney(after, before) {
    const a = decimal(after), b = decimal(before), scale = Math.max(a.scale, b.scale);
    return textDecimal(a.units * 10n ** BigInt(scale - a.scale) - b.units * 10n ** BigInt(scale - b.scale), scale);
}
export function money(value) {
    const parsed = decimal(value);
    const [whole, fraction = ''] = textDecimal(parsed.units, parsed.scale).split('.');
    return `${whole.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')},${fraction.replace(/0+$/, '').padEnd(2, '0')} ₽`;
}
export function employeeDiff(result) {
    const rows = new Map();
    for (const side of ['before', 'after']) {
        for (const day of result[side] || []) for (const employee of day.employees || []) {
            const key = `${day.location_id}/${day.date}/${employee.employee_id}`;
            const row = rows.get(key) || { key, location: day.location_id, date: day.date,
                name: employee.employee_name, before: '0', after: '0' };
            row[side] = employee.combined_net_pay;
            rows.set(key, row);
        }
    }
    return [...rows.values()].map(row => ({ ...row, delta: subtractMoney(row.after, row.before) }))
        .sort((a, b) => a.date.localeCompare(b.date) || a.location - b.location || a.key.localeCompare(b.key));
}

export function validatePatch(patch) {
    const number = (value, name, positive = false, percent = false) => {
        if ((typeof value !== 'number' && typeof value !== 'string') || String(value).trim() === ''
            || !Number.isFinite(Number(value)) || Number(value) < 0
            || (positive && Number(value) <= 0) || (percent && Number(value) > 100)) {
            throw new Error(`${name}: заполните ${percent ? 'процент от 0 до 100' : positive ? 'число больше нуля' : 'неотрицательное число'}.`);
        }
    };
    for (const [field, value] of Object.entries(patch)) {
        if (field === 'reward_bonus_enabled') {
            if (typeof value !== 'boolean') throw new Error('Неверный переключатель дополнительной премии.');
        } else if (field === 'salary_tiers') {
            if (!Array.isArray(value)) throw new Error('Неверный список зарплатных порогов.');
            let lower = 0;
            value.forEach((tier, index) => {
                number(tier.min, 'Начало порога'); number(tier.percent, 'Процент', false, true);
                if (Number(tier.min) !== lower) throw new Error('Пороги должны начинаться с 0 и продолжаться без разрывов.');
                if (index === value.length - 1) {
                    if (tier.max !== null) throw new Error('Последний порог должен действовать без верхней границы.');
                } else {
                    number(tier.max, 'Конец порога');
                    if (Number(tier.max) <= lower) throw new Error('Конец порога должен быть больше начала.');
                    lower = Number(tier.max);
                }
            });
        } else {
            for (const [key, item] of Object.entries(value || {})) {
                if (key === 'day_start' || key === 'day_end') {
                    if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(item)) throw new Error('Время начала/окончания дня: ЧЧ:ММ.');
                } else number(item, FIELD_LABELS[field] || field, key === 'per_step', key === 'revenue_percent');
            }
        }
    }
}